import { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fmtCFA } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, AlertTriangle, Apple, Boxes } from "lucide-react";
import ConfirmDelete from "@/components/ConfirmDelete";
import StockDialog from "@/components/StockDialog";
import ImageUpload from "@/components/ImageUpload";
import Thumb from "@/components/Thumb";

const CATEGORIES = ["Protéines", "Isotoniques", "Barres", "Acides Aminés", "Vitamines", "Autre"];
const EMPTY = { nom: "", categorie: "Protéines", prix: 0, stock: 0, seuil_alerte: 5, image_path: null };

export default function Nutrition() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [toDelete, setToDelete] = useState(null);
  const [stockProduit, setStockProduit] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => api.get("/produits").then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement")), []);
  useEffect(() => { refresh(); }, [refresh]);

  const openForm = (p) => {
    setEditing(p || null);
    setForm(p ? { nom: p.nom, categorie: p.categorie, prix: p.prix, stock: p.stock, seuil_alerte: p.seuil_alerte, image_path: p.image_path || null } : EMPTY);
    setDialog(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, prix: Number(form.prix), stock: Number(form.stock), seuil_alerte: Number(form.seuil_alerte) };
      if (editing) {
        await api.put(`/produits/${editing.id}`, payload);
        toast.success("Produit modifié");
      } else {
        await api.post("/produits", payload);
        toast.success("Produit ajouté");
      }
      setDialog(false);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/produits/${toDelete.id}`);
      toast.success("Produit supprimé");
      setToDelete(null);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!items) return <div className="text-muted-foreground text-sm animate-pulse" data-testid="nutrition-loading">Chargement…</div>;

  const alertes = items.filter((p) => p.stock <= p.seuil_alerte);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="nutrition-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Produits Nutrition</h1>
          <p className="text-sm text-muted-foreground mt-1">Stock des produits vendus à la caisse</p>
        </div>
        {can("stock_gerer") && (
        <Button data-testid="add-produit-button" onClick={() => openForm(null)} className="bg-emerald-500 hover:bg-emerald-600 text-foreground">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un produit
        </Button>
        )}
      </div>

      {alertes.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="stock-alert-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {alertes.length} produit(s) sous le seuil d'alerte : {alertes.map((p) => p.nom).join(", ")}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((p) => {
          const low = p.stock <= p.seuil_alerte;
          const pct = Math.min(100, Math.round((p.stock / Math.max(p.seuil_alerte * 3, 1)) * 100));
          return (
            <Card key={p.id} className={`bg-card border ${low ? "border-red-500/40" : "border-border"}`} data-testid={`produit-card-${p.id}`}>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <Thumb path={p.image_path} alt={p.nom} icon={Apple} className="w-14 h-14" testid={`produit-photo-${p.id}`} />
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground truncate">{p.nom}</p>
                      <p className="text-xs text-muted-foreground">{p.categorie}</p>
                    </div>
                  </div>
                  {low ? (
                    <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30 shrink-0">Stock bas</Badge>
                  ) : (
                    <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 shrink-0">En stock</Badge>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                    <span>Stock : {p.stock}</span>
                    <span>Seuil : {p.seuil_alerte}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${low ? "bg-red-500" : "bg-emerald-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-emerald-600 dark:text-emerald-400 font-bold">{fmtCFA(p.prix)}</span>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-amber-600 dark:hover:text-amber-400" data-testid={`stock-produit-${p.id}`} title="Mouvements de stock" onClick={() => setStockProduit(p)}>
                      <Boxes className="w-4 h-4" />
                    </Button>
                    {can("stock_gerer") && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-emerald-600 dark:hover:text-emerald-400" data-testid={`edit-produit-${p.id}`} onClick={() => openForm(p)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    )}
                    {can("stock_gerer") && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-600 dark:hover:text-red-400" data-testid={`delete-produit-${p.id}`} onClick={() => setToDelete(p)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle data-testid="produit-dialog-title">{editing ? "Modifier le produit" : "Ajouter un produit"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <ImageUpload value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v })} testid="produit-image-upload" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label className="text-foreground/80">Nom</Label>
              <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="produit-nom-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger data-testid="produit-categorie-select" className="bg-background border-border text-foreground"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Prix (FCFA)</Label>
              <Input type="number" min="0" value={form.prix} onChange={(e) => setForm({ ...form, prix: e.target.value })} data-testid="produit-prix-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Stock</Label>
              <Input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="produit-stock-input" className="bg-background border-border text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground/80">Seuil d'alerte</Label>
              <Input type="number" min="0" value={form.seuil_alerte} onChange={(e) => setForm({ ...form, seuil_alerte: e.target.value })} data-testid="produit-seuil-input" className="bg-background border-border text-foreground" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)} className="border-border text-foreground/80">Annuler</Button>
            <Button onClick={save} disabled={busy || !form.nom} data-testid="save-produit-button" className="bg-emerald-500 hover:bg-emerald-600 text-foreground">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={!!toDelete} onOpenChange={() => setToDelete(null)} onConfirm={remove} title={toDelete?.nom} busy={busy} />
      <StockDialog
        produit={stockProduit}
        canEdit={can("stock_gerer")}
        onClose={() => setStockProduit(null)}
        onChanged={(p) => { setStockProduit(p); refresh(); }}
      />
    </div>
  );
}
